// CC-49974: the shell holds exactly ONE export view ref, and the client-built
// PDF is ALWAYS rendered from it (ReportExport.jsx — there is no server PDF).
// Two grids mounted at once therefore RACE for it, and the loser's half simply
// vanishes from the PDF: a balance sheet exported with no assets in it.
//
// So the side-by-side layout publishes the two halves CONCATENATED, in CB's own
// order. Every field of the view is a flat array over leaves/groups/footers, the
// halves come from ONE parsed run and share one column set, and each value is
// already the server's — so joining them reproduces the canonical single-column
// statement exactly, which is what the export is specified to be (spec 061 §6).
// Nothing is recomputed and no figure is re-derived here.
//
// Spec 127 S5: the halves are now CB's own groups — [Assets] and [Liabilities,
// Equities] + the L&E grand total — so the grouped body (`outline` / `outlineAll`,
// the PDF's heading → rows → subtotal list) joins the same way, and "Grand Total:
// Assets" travels INSIDE the assets half's outline (it closes the Assets group)
// rather than as a footer row. The report footer is the liabilities half's alone,
// so each column's `serverTotal` (the export's totals-row fallback, read from the
// report footer) is taken from whichever half has one — the primary's first.
const mergeExportViews = (primary, secondary) => {
  if (!primary) return secondary || null
  if (!secondary) return primary
  const join = (k) => [...(primary[k] || []), ...(secondary[k] || [])]
  const hasTotal = (c) => c?.serverTotal != null && String(c.serverTotal).trim() !== ''
  const otherColumns = new Map((secondary.columns || []).map((c) => [c.key, c]))
  let columnsChanged = false
  const columns = (primary.columns || []).map((c) => {
    const other = otherColumns.get(c.key)
    if (hasTotal(c) || !hasTotal(other)) return c
    columnsChanged = true
    return { ...c, serverTotal: other.serverTotal }
  })
  return {
    ...primary, // columns / currencyOverride: identical between halves by construction
    // Unchanged columns keep the primary's array itself (a consumer may compare it).
    ...(columnsChanged ? { columns } : {}),
    groups: join('groups'),
    printRows: join('printRows'),
    printRowsAll: join('printRowsAll'),
    visibleRows: join('visibleRows'),
    allRows: join('allRows'),
    // The statement's footer block in CB's order — the same block the single grid
    // sends (since spec 127 S5, "Grand Total: Liabilities & Equity" alone).
    footerRows: join('footerRows'),
    outline: join('outline'),
    outlineAll: join('outlineAll'),
  }
}

export default mergeExportViews
